import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { AppPage, LongPullDocumentBuildQueue, NavBar, NotificationsList, SideNavbar } from '../../components';
import CircularLoader from '../../shared/components/CircularLoader';

const StyledNotificationsWrapper = styled.div<{ $sideNavWidth: number; $topNavHeight: number }>`
  position: fixed;
  top: ${({ $topNavHeight }) => ($topNavHeight ? `${$topNavHeight}px` : '60px')};
  left: ${({ $sideNavWidth }) => ($sideNavWidth ? `${$sideNavWidth}px` : '62px')};
  width: calc(100vw - 62px);
  margin: 0 auto;
  z-index: 100;
`;

const StyledContentWrapper = styled.div<{ $contentPadding: string | undefined }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.large};
  padding-top: ${({ theme }) => theme.spacing.large};
  padding: ${({ $contentPadding }) => $contentPadding && $contentPadding};
`;

const StyledMainGrid = styled.div`
  display: flex;
  min-height: 100vh;
`;

const AssetManagerLayout = () => {
  const location = useLocation();
  const navBarRef = useRef<HTMLDivElement>(null);
  const sideNavRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const [sideNavWidth, setSideNavWidth] = useState(62); // Default width
  const [topNavbarHeight, setTopNavbarHeight] = useState(0);
  const [notificationsHeight, setNotificationsHeight] = useState(0);

  // region EFFECTS
  useLayoutEffect(() => {
    if (navBarRef?.current?.clientHeight) {
      setTopNavbarHeight(navBarRef.current.clientHeight);
    }
  }, [navBarRef?.current?.clientHeight]);

  useEffect(() => {
    if (!notificationsRef.current) return;

    const element = notificationsRef.current;

    const observer = new ResizeObserver(() => {
      setNotificationsHeight(element.offsetHeight);
    });

    observer.observe(element);

    setNotificationsHeight(element.offsetHeight);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!sideNavRef.current) return;

    const element = sideNavRef.current;

    const observer = new ResizeObserver(() => {
      setSideNavWidth(element.offsetWidth);
    });

    observer.observe(element);

    // Set initial width immediately
    setSideNavWidth(element.offsetWidth);

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (window.scrollY > 0) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
  }, [location.pathname]);
  //endregion

  return (
    <>
      <NavBar ref={navBarRef} />

      <StyledNotificationsWrapper ref={notificationsRef} $sideNavWidth={sideNavWidth} $topNavHeight={topNavbarHeight}>
        <NotificationsList />
        <LongPullDocumentBuildQueue />
      </StyledNotificationsWrapper>

      <StyledMainGrid>
        <SideNavbar ref={sideNavRef} topNavbarHeight={topNavbarHeight} />

        <AppPage>
          <StyledContentWrapper $contentPadding={'0'} style={{ marginTop: notificationsHeight }}>
            <Suspense fallback={<CircularLoader centered />}>
              <Outlet />
            </Suspense>
          </StyledContentWrapper>
        </AppPage>
      </StyledMainGrid>
    </>
  );
};

export default AssetManagerLayout;
